// PugSoftReservePage.tsx
// Minimal page for PUGs: validates token → shows name/spec form OR drops into SoftReservePage
import React, { useEffect, useState, useCallback } from "react";
import SoftReservePage from "./SoftReservePage";
import { requestOnboardingTour } from "./OnboardingTour";
import { API_URL } from "./config";
import { getWowClassColor } from "./wowClassColor";
import {
  FieldLabel,
  MODAL_FIELD_CLS,
  CrossfadeImage,
  modalShellStyle,
  optionIconUrl,
  isLightHex,
  getFactionBannerColor,
  getZamClassIconUrl,
} from "./modalChrome";

const CLASS_BG = `${API_URL}/class-backgrounds`;
const CLASS_ART: Record<string, string> = {
  Warrior: `${CLASS_BG}/Warrior.png`,
  Paladin: `${CLASS_BG}/Paladin.png`,
  Hunter: `${CLASS_BG}/hunter.png`,
  Rogue: `${CLASS_BG}/rogue.png`,
  Priest: `${CLASS_BG}/priest.png`,
  "Death Knight": `${CLASS_BG}/deathknight.png`,
  Shaman: `${CLASS_BG}/shaman.png`,
  Mage: `${CLASS_BG}/mage.png`,
  Warlock: `${CLASS_BG}/warlock.png`,
  Monk: `${CLASS_BG}/monk.png`,
  Druid: `${CLASS_BG}/druid.png`,
  "Demon Hunter": `${CLASS_BG}/demonhunter.png`,
  Evoker: `${CLASS_BG}/evoker.png`,
};

function getClassArtUrl(classLabel: string | null | undefined): string | null {
  if (!classLabel) return null;
  const norm = classLabel.trim().toLowerCase().replace(/\s+/g, "");
  const key = Object.keys(CLASS_ART).find((k) => k.toLowerCase().replace(/\s+/g, "") === norm);
  return key ? CLASS_ART[key] : null;
}

function resolveMediaUrl(raw: string | null | undefined): string | null {
  const s = String(raw || "").trim();
  if (!s) return null;
  return s.startsWith("http") ? s : `${API_URL}${s}`;
}

function PugInviteHeader({
  classSplash,
  raidImageUrl,
  guildLogoUrl,
  accent,
  hasClass,
  eyebrow,
  title,
  subline,
}: {
  classSplash: string | null;
  raidImageUrl: string | null;
  guildLogoUrl: string | null;
  accent: string;
  hasClass: boolean;
  eyebrow: string;
  title: string;
  subline: string;
}) {
  return (
    <div className="relative h-48 shrink-0 overflow-hidden">
      <div className="char-modal-splash-idle absolute inset-0 z-0" />
      <CrossfadeImage
        src={!classSplash && !raidImageUrl ? guildLogoUrl : null}
        className="absolute inset-0 z-0 h-full w-full"
        imgClassName="absolute inset-0 h-full w-full object-cover object-center scale-125 blur-md"
      />
      <CrossfadeImage
        src={!classSplash ? raidImageUrl : null}
        className="absolute inset-0 z-0 h-full w-full"
        imgClassName="absolute inset-0 h-full w-full object-cover [object-position:center_18%] scale-110"
      />
      <CrossfadeImage
        src={classSplash}
        className="absolute inset-0 z-0 h-full w-full"
        imgClassName="absolute inset-0 h-full w-full object-cover [object-position:center_18%] scale-110"
      />
      <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-[#09090b] via-[#09090b]/35 to-black/10" />
      <div className="relative z-[2] h-full flex items-end gap-3 p-5">
        <div
          className="char-modal-tone relative w-14 h-14 rounded-xl border overflow-hidden shrink-0 bg-black/40"
          style={
            guildLogoUrl || hasClass
              ? { borderColor: `${accent}66`, boxShadow: `0 0 18px ${accent}44` }
              : { borderColor: "rgba(255,255,255,0.1)", boxShadow: "none" }
          }
        >
          {guildLogoUrl ? (
            <CrossfadeImage src={guildLogoUrl} className="absolute inset-0 h-full w-full" />
          ) : null}
        </div>
        <div className="min-w-0 flex-1 pb-0.5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">{eyebrow}</div>
          <h1
            className="char-modal-tone text-xl font-bold truncate leading-tight"
            style={{
              color: hasClass ? accent : "rgba(255,255,255,0.92)",
              textShadow: hasClass ? "0 1px 10px rgba(0,0,0,0.85)" : "none",
            }}
          >
            {title}
          </h1>
          {subline ? (
            <div
              className="text-xs truncate mt-0.5"
              style={{
                color: hasClass ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)",
                textShadow: hasClass ? "0 1px 8px rgba(0,0,0,0.8)" : "none",
              }}
            >
              {subline}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

type SpecOption = { id: number; key: string; label: string; metadata: any };
type FieldGroup = { id: number; key: string; label: string; options: SpecOption[] };

type UserCharacter = {
  id: string;
  name: string;
  class_name: string | null;
  spec_label: string | null;
  role_label: string | null;
  realm_name: string | null;
};

type PugInfo = {
  raidId: string;
  raidName: string;
  gameId: string;
  guildName: string;
  serverName: string | null;
  faction: string | null;
  region: string | null;
  sessions: any[] | null;
  existingPugCharacter: { character_id: string; character_name: string; class_name?: string } | null;
  userCharacters: UserCharacter[];
};

type Props = {
  raidId: string;
  token: string;
  me: any;
  onDone: () => void;
};

export default function PugSoftReservePage({ raidId, token, me, onDone }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pugInfo, setPugInfo] = useState<PugInfo | null>(null);

  // Form state
  const [fields, setFields] = useState<FieldGroup[]>([]);
  const [charName, setCharName] = useState("");
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [selectedSpec, setSelectedSpec] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Step: 'pick' = choose existing or create new, 'create' = new char form
  const [step, setStep] = useState<'pick' | 'create'>('pick');

  // After join — show SR
  const [joinedCharId, setJoinedCharId] = useState<string | null>(null);
  const [joinedCharName, setJoinedCharName] = useState<string | null>(null);
  const [joinedClassName, setJoinedClassName] = useState<string | null>(null);

  // Preview state (fetched without auth for the login screen)
  const [preview, setPreview] = useState<{
    raidName: string; guildName: string; serverName: string | null;
    guildLogoUrl: string | null; raidImageUrl: string | null; sessions: any[] | null;
  } | null>(null);

  // Fetch public preview (no auth needed) so login screen shows raid context
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/raids/${encodeURIComponent(raidId)}/pug-preview?token=${encodeURIComponent(token)}`);
        if (res.ok) setPreview(await res.json());
      } catch (_) {}
    })();
  }, [raidId, token]);

  // Fetch raid info + validate token
  useEffect(() => {
    if (!me) return;
    (async () => {
      try {
        const res = await fetch(`/raids/${encodeURIComponent(raidId)}/pug-info?token=${encodeURIComponent(token)}`, { credentials: "include" });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error === "invalid_token" ? "This invite link is invalid or expired." : "Failed to load raid info.");
        }
        const info: PugInfo = await res.json();
        setPugInfo(info);

        // If user already joined, go straight to SR
        if (info.existingPugCharacter) {
          setJoinedCharId(info.existingPugCharacter.character_id);
          setJoinedCharName(info.existingPugCharacter.character_name);
          setJoinedClassName(info.existingPugCharacter.class_name || null);
          setLoading(false);
          return;
        }

        // If user has no existing characters for this game, skip straight to create
        if (!info.userCharacters || info.userCharacters.length === 0) {
          setStep('create');
        }

        // Fetch game fields for spec dropdown
        const fieldsRes = await fetch(`/games/${encodeURIComponent(info.gameId)}/fields`);
        if (fieldsRes.ok) {
          const data: FieldGroup[] = await fieldsRes.json();
          setFields(data);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [raidId, token, me]);

  // Handle joining with an existing character
  const handlePickExisting = useCallback(async (char: UserCharacter) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/pug-join`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, existingCharacterId: char.id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error === "invalid_token" ? "Invalid invite link." : data.error || "Failed to join.");
      }
      const data = await res.json();
      setJoinedCharId(data.characterId);
      setJoinedCharName(char.name);
      setJoinedClassName(data.className || char.class_name || null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }, [raidId, token]);

  // Handle form submit (create new character)
  const handleJoin = useCallback(async () => {
    if (!charName.trim() || !selectedSpec) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/pug-join`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, characterName: charName.trim(), specOptionId: selectedSpec, classOptionId: selectedClass }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error === "invalid_token" ? "Invalid invite link." : data.error || "Failed to join.");
      }
      const data = await res.json();
      setJoinedCharId(data.characterId);
      setJoinedCharName(charName.trim());
      setJoinedClassName(data.className || selectedClassName || null);
      // Queue the one-time setup tour. Doesn't interrupt the pug flow — it waits
      // as a gentle pill and engages when the player lands on their Roster.
      requestOnboardingTour(charName.trim());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }, [raidId, token, charName, selectedSpec]);

  // --- Not logged in ---
  if (!me) {
    const previewSessions = (preview?.sessions || []).map((s: any) => {
      const d = new Date(s.starts_at);
      return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} @ ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
    });
    const guildLogoUrl = resolveMediaUrl(preview?.guildLogoUrl);
    const raidImageUrl = resolveMediaUrl(preview?.raidImageUrl);
    const accent = "#A3A3A3";
    const subline = preview
      ? [preview.guildName, preview.serverName].filter(Boolean).join(" · ")
      : "Sign in with Discord to continue.";

    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] w-full mx-auto p-4 max-w-xl">
        <div className="char-modal-shell w-full bg-[#09090b] border rounded-xl overflow-hidden" style={modalShellStyle("#27272a")}>
          <PugInviteHeader
            classSplash={null}
            raidImageUrl={raidImageUrl}
            guildLogoUrl={guildLogoUrl}
            accent={accent}
            hasClass={false}
            eyebrow="Raid Invite"
            title={preview?.raidName || "Raid Invite"}
            subline={subline}
          />
          <div className="p-6 space-y-5">
            {previewSessions.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {previewSessions.map((s: string, i: number) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[10px] text-white/45">
                    {s}
                  </span>
                ))}
              </div>
            )}
            <p className="text-sm text-white/55">
              {preview ? "Sign in to join this raid and reserve loot." : "Sign in with Discord to view the raid and reserve loot."}
            </p>
            <a
              href={`${API_URL}/auth/discord`}
              onClick={() => sessionStorage.setItem("pending_pug_sr", JSON.stringify({ raidId, token }))}
              className="flex items-center justify-center gap-3 w-full py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold rounded-xl transition-all"
            >
              <svg className="w-5 h-5 opacity-80" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z" />
              </svg>
              Log in with Discord
            </a>
          </div>
        </div>
      </div>
    );
  }

  // --- Loading ---
  if (loading) {
    return <div className="p-10 text-center text-white/40 animate-pulse">Loading raid...</div>;
  }

  // --- Error ---
  if (error && !pugInfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] w-full mx-auto p-4 max-w-xl">
        <div className="char-modal-shell w-full bg-[#09090b] border rounded-xl overflow-hidden p-8 text-center space-y-4" style={modalShellStyle("#27272a")}>
          <h2 className="text-lg font-bold text-white">This invite could not be loaded</h2>
          <p className="text-sm text-red-300">{error}</p>
          <button
            type="button"
            onClick={onDone}
            className="px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // --- Already joined / just joined → Show SR directly ---
  if (joinedCharId && pugInfo) {
    const myChars = [{ id: joinedCharId, name: joinedCharName || charName.trim(), class_name: joinedClassName }];
    return (
      <SoftReservePage
        raidId={pugInfo.raidId}
        raidInfo={{
          id: pugInfo.raidId,
          name: pugInfo.raidName,
          guild_id: "", // PUG doesn't need guild context for SR
          game_id: pugInfo.gameId,
          raid_option_id: 0,
          raidName: pugInfo.raidName,
          hostedByGuild: pugInfo.guildName,
          sessions: pugInfo.sessions || undefined,
        }}
        canManage={false}
        myCharacters={myChars}
        enrolledCharacterId={joinedCharId}
        onBack={onDone}
      />
    );
  }

  if (!pugInfo) return null;

  // --- Join Form ---
  const specGroup = fields.find(g => g.key === "spec");
  const classGroup = fields.find(g => g.key === "class");

  // Resolve selected class key for filtering specs
  const selectedClassOption = classGroup?.options.find(c => c.id === selectedClass);
  const selectedClassKey = selectedClassOption?.key || null;
  const selectedClassName = selectedClassOption?.label || null;

  // Filter specs to only those allowed for the selected class
  const visibleSpecs = specGroup?.options.filter(opt => {
    if (!selectedClassKey) return false;
    const allowed = opt.metadata?.allowed_class_keys;
    if (allowed && allowed.length > 0) return allowed.includes(selectedClassKey);
    return false;
  }) || [];

  const sessionStrings = (pugInfo.sessions || []).map((s: any) => {
    const d = new Date(s.starts_at);
    return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} @ ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
  });

  const hasClass = Boolean(selectedClassName);
  const accent = hasClass
    ? getWowClassColor(selectedClassName)
    : pugInfo.faction
      ? getFactionBannerColor(pugInfo.faction)
      : "#A3A3A3";
  const shellAccent = hasClass || pugInfo.faction ? accent : "#27272a";
  const ctaColor = isLightHex(accent) ? "#111" : "#fff";
  const classSplash = getClassArtUrl(selectedClassName);
  const guildLogoUrl = resolveMediaUrl(preview?.guildLogoUrl);
  const raidImageUrl = resolveMediaUrl(preview?.raidImageUrl);
  const headerTitle = charName.trim() || pugInfo.raidName;
  const headerSub = [pugInfo.guildName, pugInfo.serverName, pugInfo.faction].filter(Boolean).join(" · ");
  const showingPick = step === "pick" && pugInfo.userCharacters && pugInfo.userCharacters.length > 0;

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] w-full mx-auto p-4 max-w-xl">
      <div
        className="char-modal-shell w-full bg-[#09090b] border rounded-xl overflow-hidden"
        style={modalShellStyle(shellAccent)}
      >
        <PugInviteHeader
          classSplash={showingPick ? null : classSplash}
          raidImageUrl={raidImageUrl}
          guildLogoUrl={guildLogoUrl}
          accent={accent}
          hasClass={showingPick ? false : hasClass}
          eyebrow={pugInfo.raidName}
          title={showingPick ? pugInfo.raidName : headerTitle}
          subline={headerSub}
        />

        <div className="p-6 space-y-5">
          {sessionStrings.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {sessionStrings.map((s: string, i: number) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[10px] text-white/45">
                  {s}
                </span>
              ))}
            </div>
          )}

          {showingPick ? (
            <>
              <div>
                <h2 className="text-lg font-bold text-white">Select a Character</h2>
                <p className="text-xs text-white/40 mt-1">Pick one of your existing characters or create a new one for this run.</p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm">{error}</div>
              )}

              <div className="space-y-2">
                {pugInfo.userCharacters.map((char) => {
                  const color = getWowClassColor(char.class_name);
                  const icon = getZamClassIconUrl(char.class_name);
                  return (
                    <button
                      key={char.id}
                      type="button"
                      onClick={() => handlePickExisting(char)}
                      disabled={submitting}
                      className="char-modal-tone w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left disabled:opacity-50"
                      style={{ borderColor: "rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.03)" }}
                    >
                      <div
                        className="relative w-10 h-10 rounded-lg overflow-hidden border shrink-0 bg-black/40"
                        style={{ borderColor: `${color}55` }}
                      >
                        {icon ? <img src={icon} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate" style={{ color }}>
                          {char.name}
                        </div>
                        <div className="text-[11px] text-white/40 truncate">
                          {[char.spec_label, char.class_name, char.realm_name].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-white/35 shrink-0">Select</span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => setStep("create")}
                className="w-full py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white/70 hover:text-white font-bold text-xs uppercase tracking-widest"
              >
                Create New Character
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold text-white">Your Character</h2>
                {pugInfo.userCharacters && pugInfo.userCharacters.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setStep("pick")}
                    className="text-xs text-white/40 hover:text-white"
                  >
                    Back
                  </button>
                )}
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm">{error}</div>
              )}

              <div className="space-y-1.5">
                <FieldLabel>Character Name</FieldLabel>
                <input
                  value={charName}
                  onChange={(e) => setCharName(e.target.value)}
                  placeholder="Name"
                  maxLength={24}
                  className={`${MODAL_FIELD_CLS} text-base font-semibold`}
                />
              </div>

              <div className="space-y-1.5">
                <FieldLabel>Realm</FieldLabel>
                <div className={`${MODAL_FIELD_CLS} text-white/50`}>
                  {pugInfo.serverName || "Unknown"}
                  <span className="text-white/25 ml-2">(set by the guild)</span>
                </div>
              </div>

              {classGroup ? (
                <div className="space-y-2">
                  <FieldLabel>Class</FieldLabel>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {classGroup.options.map((opt) => {
                      const color = getWowClassColor(opt.label);
                      const selected = selectedClass === opt.id;
                      const icon = optionIconUrl(opt, opt.label);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          title={opt.label}
                          onClick={() => {
                            setSelectedClass(opt.id);
                            setSelectedSpec(null);
                          }}
                          className="char-modal-tone relative aspect-square rounded-xl border overflow-hidden bg-black/40"
                          style={
                            selected
                              ? { borderColor: color, boxShadow: `0 0 16px ${color}66` }
                              : { borderColor: "rgba(255,255,255,0.1)" }
                          }
                        >
                          {icon ? (
                            <img src={icon} alt="" className="absolute inset-0 h-full w-full object-cover" />
                          ) : (
                            <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold" style={{ color }}>
                              {opt.label.charAt(0)}
                            </span>
                          )}
                          <span className="absolute inset-x-0 bottom-0 pt-4 pb-1 px-0.5 bg-gradient-to-t from-black/90 via-black/55 to-transparent">
                            <span
                              className="block text-[10px] font-medium leading-tight text-center"
                              style={{
                                color: selected ? color : "rgba(255,255,255,0.82)",
                                textShadow: "0 1px 4px rgba(0,0,0,0.9)",
                              }}
                            >
                              {opt.label}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-white/30">Loading...</div>
              )}

              {selectedClass ? (
                <div className="space-y-2">
                  <FieldLabel>Specialization</FieldLabel>
                  {visibleSpecs.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {visibleSpecs.map((spec) => {
                        const isSelected = selectedSpec === spec.id;
                        const icon = optionIconUrl(spec, selectedClassName);
                        return (
                          <button
                            key={spec.id}
                            type="button"
                            onClick={() => setSelectedSpec(spec.id)}
                            className="char-modal-tone flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border"
                            style={
                              isSelected
                                ? { borderColor: accent, backgroundColor: `${accent}33`, color: "#fff" }
                                : {
                                    backgroundColor: "rgba(0,0,0,0.4)",
                                    borderColor: "rgba(255,255,255,0.1)",
                                    color: "rgba(255,255,255,0.6)",
                                  }
                            }
                          >
                            {icon && <img src={icon} alt="" className="w-4 h-4 rounded" />}
                            {spec.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-white/30">No specs available for this class.</div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-white/30">Select a class to see specs.</div>
              )}

              <button
                type="button"
                onClick={handleJoin}
                disabled={!charName.trim() || !selectedSpec || submitting}
                className="char-modal-tone w-full py-3 font-bold text-xs uppercase tracking-widest rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: accent, color: ctaColor }}
              >
                {submitting ? "Joining..." : "Join and Reserve Loot"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
